import type {
  Container,
  ContainerSearchResult,
  FileEntry,
  ItemSearchResult,
  SearchResult,
} from "@egalregal/shared";
import type { SearchScope } from "./searchUrl";

/**
 * One photo in the search results: a photo file attached to a matching
 * container. The image opens the owning container's lightbox; the caption
 * opens that container's detail (spec §5).
 */
export interface SearchPhotoHit {
  container: Container;
  file: FileEntry;
  /** Display path of the owning container. */
  path: string;
}

export interface SearchGroups {
  containers: ContainerSearchResult[];
  items: ItemSearchResult[];
  photos: SearchPhotoHit[];
}

/**
 * Split the flat ranked result list into the scope groups, honouring the
 * selected scope. Photos are the photo files of the matching containers, so
 * they appear with the containers they belong to; a container hit without
 * photos contributes no photo group.
 *
 * @param results the ranked search results from the store.
 * @param scope the URL-selected scope.
 * @returns the groups to render; hidden groups are empty.
 */
export function groupSearchResults(
  results: readonly SearchResult[],
  scope: SearchScope,
): SearchGroups {
  const containerHits = results.filter(isContainerHit);
  const showContainers = scope === "all" || scope === "containers";
  const showItems = scope === "all" || scope === "items";
  const showPhotos = scope === "all" || scope === "photos";

  return {
    containers: showContainers ? containerHits : [],
    items: showItems ? results.filter(isItemHit) : [],
    photos: showPhotos ? containerHits.flatMap(photoHitsOf) : [],
  };
}

/** Whether any group has something to render. */
export function hasGroupedResults(groups: SearchGroups): boolean {
  return groups.containers.length + groups.items.length + groups.photos.length > 0;
}

function isContainerHit(result: SearchResult): result is ContainerSearchResult {
  return result.type === "container";
}

function isItemHit(result: SearchResult): result is ItemSearchResult {
  return result.type === "item";
}

function photoHitsOf(hit: ContainerSearchResult): SearchPhotoHit[] {
  return hit.container.files
    .filter((file) => file.kind === "photo")
    .map((file) => ({ container: hit.container, file, path: hit.path }));
}
